import React, { useState } from 'react';
import { Card } from './SubscriptionAppCard/Card';
import { Input } from './SubscriptionAppInput/Input';
import { Button } from './SubscriptionAppButton';
import { LayoutGrid, Package, Check, Info, Image as ImageIcon, ChevronDown, ArrowRight } from 'lucide-react';
interface ClubBuilderBasicsPanelProps {
  onNext?: () => void;
  boxType?: 'custom' | 'curated';
  onBoxTypeChange?: (type: 'custom' | 'curated') => void;
}
export function ClubBuilderBasicsPanel({
  onNext,
  boxType: controlledBoxType,
  onBoxTypeChange
}: ClubBuilderBasicsPanelProps) {
  const [internalBoxType, setInternalBoxType] = useState<'custom' | 'curated'>('custom');
  const boxType = controlledBoxType ?? internalBoxType;
  const setBoxType = (type: 'custom' | 'curated') => {
    onBoxTypeChange?.(type);
    setInternalBoxType(type);
  };
  const [boxName, setBoxName] = useState('');
  const [validationError, setValidationError] = useState('');
  const handleNext = () => {
    if (!boxName.trim()) {
      setValidationError('Box Name is required');
      return;
    }
    setValidationError('');
    onNext?.();
  };
  return <div className="flex flex-col gap-6" data-id="element-900" data-mp="2n6w8n6jfqhjj">
      {/* Card 1: Club Details */}
      <Card padding="md" className="border border-[#E2E8F0]" data-id="element-901" data-mp="2s70qp0zzrkbv">
        <div className="mb-5" data-id="element-902" data-mp="2s5fyvj52afhp">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-903" data-mp="2w9dsxffj503w">
            Club Details
          </h2>
          <p className="text-[13px] text-[#64748B] mt-1" data-id="element-904" data-mp="1zjefp7ofpdez">
            The name and description shown to customers during the signup flow
          </p>
        </div>

        <div className="space-y-6" data-id="element-905" data-mp="28jb8nlhuwyfu">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-906" data-mp="28i8gla8sspyc">
            <div data-id="element-907" data-mp="2d9rk5024a9zk">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-908" data-mp="2da9pcm6rbyjm">
                Box Name <span className="text-[#EF4444]" data-id="element-909" data-mp="1gsn8ocrx5z3r">*</span>
              </label>
              <input type="text" placeholder="e.g. Estate Wine Club" value={boxName} onChange={e => {
              setBoxName(e.target.value);
              if (validationError) setValidationError('');
            }} className={`w-full h-[40px] px-3 bg-white border rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 ${validationError ? 'border-[#EF4444]' : 'border-[#CBD5E1]'}`} data-id="element-910" data-mp="1ktp8aupr0d69" />

              {validationError && <p className="mt-1.5 text-[12px] text-[#EF4444]" data-id="element-911" data-mp="255ietxb1z7g5">
                  {validationError}
                </p>}
            </div>
          </div>

          <div data-id="element-912" data-mp="2088h7udifph8">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-913" data-mp="208s13gmq0nxw">
              Description
            </label>
            <textarea className="w-full h-[100px] px-3 py-2 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 resize-y" placeholder="Describe your wine club to customers..." defaultValue="" data-id="element-914" data-mp="2a53bu26nx5q8" />

            <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-915" data-mp="3t2hth0mf00gf">
              Displayed beneath the club name on the signup widget
            </p>
          </div>

          <div data-id="element-916" data-mp="3j5d6wzonq2z3">
            <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-917" data-mp="3j5v9sf5trffz">
              URL Slug
            </label>
            <div className="flex rounded-[6px] overflow-hidden border border-[#CBD5E1] focus-within:border-[#0EA5E9] focus-within:ring-[3px] focus-within:ring-[#0EA5E9]/20" data-id="element-918" data-mp="3o57w8yssaki1">
              <div className="bg-[#F8FAFC] px-3 py-2 text-[14px] text-[#64748B] border-r border-[#CBD5E1] flex items-center" data-id="element-919" data-mp="ko96q96kq3w6">
                yourstore.com/apps/winepik/
              </div>
              <input type="text" className="flex-1 px-3 py-2 text-[14px] text-[#0F172A] focus:outline-none" placeholder="my-wine-club" defaultValue="" data-id="element-920" data-mp="axmver5w3zja" />
            </div>
            <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-921" data-mp="yledkbe436sh">
              Auto-generated from the name. Used in the signup page URL.
            </p>
          </div>
        </div>
      </Card>

      {/* Card 2: Box Type */}
      <Card padding="md" className="border border-[#E2E8F0]" data-id="element-922" data-mp="36wgqdu43cmbm">
        <div className="mb-5" data-id="element-923" data-mp="36uvvg3dv71d8">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-924" data-mp="31wucrjkcp42a">Box Type</h2>
          <p className="text-[13px] text-[#64748B] mt-1" data-id="element-925" data-mp="31yxdaxj4qfl9">
            Choose how wines are selected for this club
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-926" data-mp="3um1bojnpvvo2">
          {/* Custom Box Option */}
          <div onClick={() => setBoxType('custom')} className={`
              relative p-5 rounded-lg border-2 cursor-pointer transition-all duration-200
              ${boxType === 'custom' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}
            `} data-id="element-927" data-mp="3pnq0o5lorf49">
            <div className="flex justify-between items-start mb-3" data-id="element-928" data-mp="h752ljule6fx">
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center
                ${boxType === 'custom' ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'bg-[#F1F5F9] text-[#64748B]'}
              `} data-id="element-929" data-mp="cg507z2pew61">
                <LayoutGrid className="w-6 h-6" data-id="element-930" data-mp="opme1vm13vbj" />
              </div>
              <div className={`
                w-5 h-5 rounded-full flex items-center justify-center border
                ${boxType === 'custom' ? 'bg-[#0EA5E9] border-[#0EA5E9] text-white' : 'border-[#CBD5E1] bg-white'}
              `} data-id="element-931" data-mp="tph4xz5t0kwl">
                {boxType === 'custom' && <Check className="w-3 h-3" data-id="element-932" data-mp="13nermi80m9n3" />}
              </div>
            </div>
            <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1" data-id="element-933" data-mp="17nxj2b5zxdor">
              Custom Box
            </h3>
            <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-934" data-mp="1cgqnsz1gsm0u">
              Customer chooses their own wines from a product collection. They
              pick what goes in the box.
            </p>
          </div>

          {/* Curated Box Option */}
          <div onClick={() => setBoxType('curated')} className={`
              relative p-5 rounded-lg border-2 cursor-pointer transition-all duration-200
              ${boxType === 'curated' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}
            `} data-id="element-935" data-mp="1rcg5wm3j4f0a">
            <div className="flex justify-between items-start mb-3" data-id="element-936" data-mp="1bf58qhvine2o">
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center
                ${boxType === 'curated' ? 'bg-[#E0F2FE] text-[#0EA5E9]' : 'bg-[#F1F5F9] text-[#64748B]'}
              `} data-id="element-937" data-mp="16o6tllm9km10">
                <Package className="w-6 h-6" data-id="element-938" data-mp="1fha2rhpvmcpu" />
              </div>
              <div className={`
                w-5 h-5 rounded-full flex items-center justify-center border
                ${boxType === 'curated' ? 'bg-[#0EA5E9] border-[#0EA5E9] text-white' : 'border-[#CBD5E1] bg-white'}
              `} data-id="element-939" data-mp="1udrcy5mbxlat">
                {boxType === 'curated' && <Check className="w-3 h-3" data-id="element-940" data-mp="77h74flkn" />}
              </div>
            </div>
            <h3 className="text-[14px] font-semibold text-[#0F172A] mb-1" data-id="element-941" data-mp="3r7cnzv43dx01">
              Curated Box
            </h3>
            <p className="text-[13px] text-[#64748B] leading-relaxed" data-id="element-942" data-mp="26osegdtn3xs4">
              You select the wines — the customer receives a pre-built pack.
              Great for tasting experiences.
            </p>
          </div>
        </div>

        <div className="bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-943" data-mp="21x0lxxqxbhzc">
          <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0" data-id="element-944" data-mp="1nxish52ongjn" />
          <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-945" data-mp="1nw7p36d5gat2">
            <span className="font-semibold text-[#0F172A]" data-id="element-946" data-mp="1s05oli6dznmn">Custom boxes</span>{' '}
            show a product browser where customers pick wines.{' '}
            <span className="font-semibold text-[#0F172A]" data-id="element-947" data-mp="1e1o7i3azl9dp">Curated boxes</span>{' '}
            display pre-selected wines as read-only. Selling plans and pricing
            work the same for both types.
          </p>
        </div>
      </Card>

      {/* Card 3: Box Image */}
      <Card padding="md" className="border border-[#E2E8F0]" data-id="element-948" data-mp="3dbnymmuzjyjh">
        <div className="mb-5" data-id="element-949" data-mp="205uflurm1yud">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-950" data-mp="2052q91olwxcj">
            Box Image
          </h2>
          <p className="text-[13px] text-[#64748B] mt-1" data-id="element-951" data-mp="1w4smzws78l1d">
            Featured image shown on the signup page and customer portal
          </p>
        </div>

        <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-12 flex flex-col items-center justify-center text-center hover:bg-[#F8FAFC] transition-colors cursor-pointer" data-id="element-952" data-mp="1mezdr2fj4i0a">
          <div className="w-12 h-12 bg-[#F1F5F9] rounded-lg flex items-center justify-center mb-4 text-[#94A3B8]" data-id="element-953" data-mp="1cfiaernr2g7h">
            <ImageIcon className="w-6 h-6" data-id="element-954" data-mp="18fg5m9mpx6nz" />
          </div>
          <p className="text-[14px] font-medium text-[#64748B] mb-1" data-id="element-955" data-mp="13fbnkf65w3vn">
            Click to upload or drag and drop
          </p>
          <p className="text-[12px] text-[#94A3B8]" data-id="element-956" data-mp="32spjedmarec1">
            PNG, JPG, WebP up to 5MB · Recommended 1200 x 630px
          </p>
        </div>
      </Card>

      {/* Card 4: Storefront Settings */}
      <Card padding="md" className="border border-[#E2E8F0]" data-id="element-957" data-mp="35dnijv0lj0ic">
        <div className="mb-5" data-id="element-958" data-mp="35b1a7t5vsgz2">
          <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-959" data-mp="3abfcsvopqypp">
            Storefront Settings
          </h2>
          <p className="text-[13px] text-[#64748B] mt-1" data-id="element-960" data-mp="3f3qarszw8afm">
            Additional options for how this box appears on the storefront
          </p>
        </div>

        <div className="space-y-6" data-id="element-961" data-mp="2mdzupeepaygv">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-962" data-mp="2mf1i8icwldfh">
            <div data-id="element-963" data-mp="2rcum34qqhvcl">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-964" data-mp="2rcb2zkpcl0wz">
                Shipping Display
              </label>
              <div className="h-[40px] px-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-[6px] flex items-center gap-2" data-id="element-965" data-mp="2waw2tj0otikw">
                <svg className="w-4 h-4 text-[#10B981] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-966" data-mp="30bg2oucnb0aq">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" data-id="element-967" data-mp="fms89ceuldcf" />
                </svg>
                <span className="text-[14px] text-[#475569]" data-id="element-968" data-mp="em3fiea563vz">
                  Syncs from Shopify
                </span>
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-969" data-mp="jfy4nx8inb2g">
                Shipping rates are managed in your Shopify shipping settings and
                applied automatically at checkout.
              </p>
            </div>

            <div data-id="element-970" data-mp="3rtezpoxvru1p">
              <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-971" data-mp="3rsvgm4whuzm3">
                Availability
              </label>
              <div className="relative" data-id="element-972" data-mp="m2duwm1ysfg">
                <select className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none" defaultValue="all" data-id="element-973" data-mp="5mzu33ni5ceb">
                  <option value="all" data-id="element-974" data-mp="1gzsjw8wqfqrq">Available to all customers</option>
                  <option value="tagged" data-id="element-975" data-mp="31imldjbsi77z">Only customers with tag...</option>
                  <option value="link" data-id="element-976" data-mp="2wk1kdhoprjih">Only via direct link</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#64748B] w-4 h-4 pointer-events-none" data-id="element-977" data-mp="2rtl6xxkw7xw4" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-978" data-mp="2mui14ax39nn3">
                Restrict who can see and subscribe to this box
              </p>
            </div>
          </div>

          <Input label="Short Tagline" placeholder="e.g. Hand-picked wines from our estate vineyard" defaultValue="" data-id="element-979" data-mp="2dtaeiq3xqb8u" />

          <p className="text-[12px] text-[#94A3B8] -mt-4" data-id="element-980" data-mp="28v8ua1urw5vs">
            Optional one-liner shown beneath the box name in collection views
          </p>
        </div>
      </Card>

      {/* Next Button */}
      <div className="flex justify-end pt-2" data-id="element-981" data-mp="72udj63yt7gy">
        <Button variant="primary" onClick={handleNext} iconRight={<ArrowRight className="w-4 h-4" data-id="element-983" data-mp="22g53mygbvie" />} data-id="element-982" data-mp="2413xorsu0m2">
          Next: Pack Sizes
        </Button>
      </div>
    </div>;
}